/**
 * Platform Status Banner - the user-facing incident notification (top of the app chrome).
 *
 * Mounted in Layout + WorkspaceLayout; realtime is initialized there via initIncidentRealtime(), so this
 * updates instantly (Supabase postgres_changes on the incidents table) with no polling. It reads the REAL
 * incident state from platformStatusService (getPlatformStatus + onStatusUpdate): when Nuvra has a live
 * incident it shows a severity-styled bar saying Nuvra is aware and working on a fix (with the affected
 * services); when the incident is resolved it shows a brief "resolved" confirmation. Admins post/resolve
 * from the admin Status tab. It renders nothing when all systems are operational, and never fabricates an
 * outage. This is the "GitHub is experiencing..." pattern, built into Nuvra.
 */
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, X } from "lucide-react";
import { getPlatformStatus, onStatusUpdate, type PlatformStatusData, type PlatformIncident } from "@/services/platformStatusService";
import { useI18n } from "@/i18n/I18nContext";

const RESOLVED_WINDOW_MS = 20 * 60_000; // show a resolved confirmation for 20 minutes after it resolves
const DISMISS_KEY = "nuvra_incident_dismissed";
const RANK: Record<string, number> = { critical: 3, major: 2, minor: 1 };
const SEVERITY: Record<string, { bg: string; chip: string; label: string }> = {
  critical: { bg: "bg-red-600 text-white",     chip: "bg-black/20", label: "Critical" },
  major:    { bg: "bg-orange-500 text-white",  chip: "bg-black/20", label: "Major" },
  minor:    { bg: "bg-amber-500 text-black",   chip: "bg-black/10", label: "Notice" },
};

function readDismissed(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]")); } catch { return new Set(); }
}
function persistDismissed(ids: Set<string>): void {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...ids].slice(-50))); } catch { /* ignore */ }
}

export function PlatformStatusBanner() {
  const { t } = useI18n();
  const [status, setStatus] = useState<PlatformStatusData>(getPlatformStatus());
  const [dismissed, setDismissed] = useState<Set<string>>(readDismissed);

  useEffect(() => onStatusUpdate(setStatus), []);

  const topActive = useMemo<PlatformIncident | null>(() =>
    [...(status.activeIncidents ?? [])].sort((a, b) => (RANK[b.severity] ?? 1) - (RANK[a.severity] ?? 1))[0] ?? null,
  [status.activeIncidents]);

  const topResolved = useMemo<PlatformIncident | null>(() => {
    if (topActive) return null;
    const now = Date.now();
    return (status.recentIncidents ?? []).find(r => {
      if (dismissed.has(r.id)) return false;
      const at = r.resolvedAt ? Date.parse(r.resolvedAt) : 0;
      return at > 0 && now - at < RESOLVED_WINDOW_MS;
    }) ?? null;
  }, [topActive, status.recentIncidents, dismissed]);

  const dismiss = (id: string) => setDismissed(prev => { const next = new Set(prev); next.add(id); persistDismissed(next); return next; });

  if (topActive) {
    const sev = SEVERITY[topActive.severity] ?? SEVERITY.minor;
    const services = (topActive.services ?? []).filter(Boolean);
    return (
      <div role="status" aria-live="polite" className={`w-full ${sev.bg} relative z-50`}>
        <div className="mx-auto flex max-w-5xl items-start gap-2.5 px-4 py-1.5 pr-9">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1 text-[12px] leading-snug">
            <span className="font-semibold">{topActive.title}</span>
            {topActive.description ? <span className="opacity-95"> {topActive.description}</span> : null}
            <span className="opacity-90"> Nuvra is aware and working on a fix.</span>
            {services.length > 0 && <span className="opacity-80"> Affected: {services.join(", ")}.</span>}
          </div>
          <span className={`mt-0.5 shrink-0 rounded-full ${sev.chip} px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide`}>{sev.label}</span>
        </div>
      </div>
    );
  }

  if (topResolved) {
    return (
      <div role="status" aria-live="polite" className="w-full bg-emerald-600 text-white relative z-50">
        <div className="mx-auto flex items-center gap-2.5 px-4 py-1.5 pr-9 max-w-5xl">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1 text-[12px] leading-snug">
            <span className="font-semibold">Resolved:</span> {topResolved.title}. Everything is back to normal.
          </div>
        </div>
        <button onClick={() => dismiss(topResolved.id)} aria-label={t("psb.dismiss")}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 hover:bg-white/20">
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return null;
}
